import React, { useState, useEffect } from 'react';
import {
  collection, query, where, getDocs
} from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import '../../../styles/adminhome.css';

const getYearStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
};

const getMonthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
};

const getMonthEnd = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
};

const fmtEventDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'short' });
};

const fmtEventDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-SG', {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
  }) + ' · ' + d.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit' });
};

const fmtEventTime = (ts) => {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit' });
};

const truncate = (str, n) =>
  !str || str.length <= n ? str : str.substring(0, n) + '…';

const EVENT_TYPE_LABELS = {
  ONE_OFF:   'One-off',
  RECURRING: 'Recurring',
};

const CATEGORY_LABELS = {
  CIRCLE_TIME:       'Circle Time',
  WORKSHOP:          'Workshop',
  SUPPORT_GROUP:     'Support Group',
  SOCIAL:            'Social',
  HEALTH_WELLNESS:   'Health & Wellness',
  BEFRIENDING:       'Befriending',
  OUTREACH:          'Outreach',
  TRAINING:          'Training',
};

function EventDetailModal({ ev, onClose }) {
  if (!ev) return null;

  const startDt = ev.start_datetime?.toDate ? ev.start_datetime.toDate() : (ev.start_datetime ? new Date(ev.start_datetime) : null);
  const endDt   = ev.end_datetime?.toDate   ? ev.end_datetime.toDate()   : (ev.end_datetime   ? new Date(ev.end_datetime)   : null);

  const fmtDate = (d) => d ? d.toLocaleDateString('en-SG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—';
  const fmtTime = (d) => d ? d.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit' }) : null;

  const statusClass = {
    published: 'published',
    draft:     'draft',
    cancelled: 'cancelled',
  }[ev.status] || 'published';

  const loc = ev.location || {};
  const hasLocation = loc.name || loc.address || loc.postal_code;

  return (
    <div className="bb-ev-modal-backdrop" onClick={onClose}>
      <div className="bb-ev-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="bb-ev-modal-header">
          <div className="bb-ev-modal-title">{ev.title || ev.name || 'Event'}</div>
          <div className="bb-ev-modal-badges">
            {ev.category && (
              <span className="bb-ev-modal-badge bb-ev-modal-badge--category">
                {CATEGORY_LABELS[ev.category] || ev.category}
              </span>
            )}
            {ev.event_type && (
              <span className="bb-ev-modal-badge bb-ev-modal-badge--type">
                {EVENT_TYPE_LABELS[ev.event_type] || ev.event_type}
              </span>
            )}
            {ev.status && (
              <span className={`bb-ev-modal-badge bb-ev-modal-badge--${statusClass}`}>
                {ev.status.charAt(0).toUpperCase() + ev.status.slice(1)}
              </span>
            )}
          </div>
          <button className="bb-ev-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="bb-ev-modal-body">

          {/* Date */}
          {startDt && (
            <div className="bb-ev-modal-row">
              <span className="bb-ev-modal-icon">📅</span>
              <div className="bb-ev-modal-info">
                <span className="bb-ev-modal-info-label">Date</span>
                <span className="bb-ev-modal-info-value">{fmtDate(startDt)}</span>
              </div>
            </div>
          )}

          {/* Time */}
          {startDt && (
            <div className="bb-ev-modal-row">
              <span className="bb-ev-modal-icon">🕐</span>
              <div className="bb-ev-modal-info">
                <span className="bb-ev-modal-info-label">Time</span>
                <span className="bb-ev-modal-info-value">
                  {fmtTime(startDt)}
                  {endDt ? ` — ${fmtTime(endDt)}` : ''}
                </span>
              </div>
            </div>
          )}

          {/* Location */}
          {hasLocation && (
            <div className="bb-ev-modal-row">
              <span className="bb-ev-modal-icon">📍</span>
              <div className="bb-ev-modal-info">
                <span className="bb-ev-modal-info-label">Location</span>
                {loc.name && <span className="bb-ev-modal-info-value">{loc.name}</span>}
                {loc.address && <span className="bb-ev-modal-info-value bb-ev-modal-info-value--muted">{loc.address}{loc.postal_code ? ` · S(${loc.postal_code})` : ''}</span>}
              </div>
            </div>
          )}

          {/* Description */}
          {ev.description && (
            <>
              <div className="bb-ev-modal-divider" />
              <div className="bb-ev-modal-row">
                <span className="bb-ev-modal-icon">📝</span>
                <div className="bb-ev-modal-info">
                  <span className="bb-ev-modal-info-label">Description</span>
                  <span className="bb-ev-modal-info-value bb-ev-modal-info-value--muted" style={{ whiteSpace: 'pre-wrap' }}>
                    {ev.description}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Capacity */}
          {(ev.member_capacity != null || ev.volunteer_capacity != null) && (
            <>
              <div className="bb-ev-modal-divider" />
              <div className="bb-ev-modal-row">
                <span className="bb-ev-modal-icon">👥</span>
                <div className="bb-ev-modal-info">
                  <span className="bb-ev-modal-info-label">Capacity</span>
                  <div className="bb-ev-modal-cap-row">
                    {ev.member_capacity != null && (
                      <div className="bb-ev-modal-cap-item">
                        <span className="bb-ev-modal-info-value">{ev.member_capacity}</span>
                        <span className="bb-ev-modal-info-label">Members</span>
                      </div>
                    )}
                    {ev.volunteer_capacity != null && (
                      <div className="bb-ev-modal-cap-item">
                        <span className="bb-ev-modal-info-value">{ev.volunteer_capacity}</span>
                        <span className="bb-ev-modal-info-label">Volunteers</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Tags */}
          {ev.tags && (
            <div className="bb-ev-modal-row">
              <span className="bb-ev-modal-icon">🏷️</span>
              <div className="bb-ev-modal-info">
                <span className="bb-ev-modal-info-label">Tags</span>
                <span className="bb-ev-modal-info-value bb-ev-modal-info-value--muted">{ev.tags}</span>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function AdminHome({ orgId, heroImageUrl, onGoToImages }) {
  const [memberMetrics,    setMemberMetrics]    = useState(null);
  const [volunteerMetrics, setVolunteerMetrics] = useState(null);
  const [monthEvents,      setMonthEvents]      = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [selectedEvent,    setSelectedEvent]    = useState(null);

  useEffect(() => {
    if (!orgId) return;
    loadAllMetrics();
  }, [orgId]);

  const loadAllMetrics = async () => {
    setLoading(true);
    await Promise.all([
      loadMemberMetrics(),
      loadVolunteerMetrics(),
      loadMonthEvents(),
    ]);
    setLoading(false);
  };

  const loadMemberMetrics = async () => {
    try {
      const yearStart = getYearStart();
      const snap = await getDocs(
        query(collection(db, 'members'), where('orgId', '==', orgId))
      );
      const members = snap.docs.map(d => d.data());
      const total   = members.length;
      const currentYear = new Date().getFullYear();
      const newThisYear = members.filter(m => {
        // Prefer dateJoined (YYYY-MM-DD string), fall back to createdAt timestamp
        if (m.dateJoined) {
          return m.dateJoined.startsWith(String(currentYear));
        }
        const raw = m.createdAt;
        if (!raw) return false;
        const d = raw.toDate ? raw.toDate() : new Date(raw);
        return d >= yearStart;
      }).length;
      const highEngage   = members.filter(m => m.engagementTier === 'high').length;
      const medEngage    = members.filter(m => m.engagementTier === 'medium').length;
      const pctHigh   = total > 0 ? ((highEngage / total) * 100).toFixed(1) : '0.0';
      const pctMedium = total > 0 ? ((medEngage  / total) * 100).toFixed(1) : '0.0';
      setMemberMetrics({ total, newThisYear, pctHigh, pctMedium });
    } catch (err) {
      console.error('AdminHome: member metrics error', err);
      setMemberMetrics({ total: 0, newThisYear: 0, pctHigh: '0.0', pctMedium: '0.0' });
    }
  };

  const loadVolunteerMetrics = async () => {
    try {
      const yearStart = getYearStart();
      const snap = await getDocs(
        query(collection(db, 'volunteers'), where('orgId', '==', orgId))
      );
      const vols = snap.docs.map(d => d.data());
      const total = vols.length;
      const newThisYear = vols.filter(v => {
        const raw = v.createdAt;
        if (!raw) return false;
        const d = raw.toDate ? raw.toDate() : new Date(raw);
        return d >= yearStart;
      }).length;
      const highEngage = vols.filter(v => { const h = v.totalHours || 0; return h >= 51 && h <= 100; }).length;
      const medEngage  = vols.filter(v => { const h = v.totalHours || 0; return h >= 1  && h <= 50;  }).length;
      const pctHigh   = total > 0 ? ((highEngage / total) * 100).toFixed(1) : '0.0';
      const pctMedium = total > 0 ? ((medEngage  / total) * 100).toFixed(1) : '0.0';
      setVolunteerMetrics({ total, newThisYear, pctHigh, pctMedium });
    } catch (err) {
      console.error('AdminHome: volunteer metrics error', err);
      setVolunteerMetrics({ total: 0, newThisYear: 0, pctHigh: '0.0', pctMedium: '0.0' });
    }
  };

  const loadMonthEvents = async () => {
    try {
      const now = new Date();
      now.setHours(0, 0, 0, 0); // start of today — only show today or future
      const snap = await getDocs(
        query(collection(db, 'events'), where('orgId', '==', orgId))
      );
      const seen = new Set();
      const events = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(ev => {
          if (!ev.start_datetime) return false;
          const d = ev.start_datetime.toDate
            ? ev.start_datetime.toDate()
            : new Date(ev.start_datetime);
          if (d < now) return false;          // exclude past events
          if (seen.has(ev.id)) return false;  // deduplicate by doc id
          seen.add(ev.id);
          return true;
        })
        .sort((a, b) => {
          const da = a.start_datetime?.toDate ? a.start_datetime.toDate() : new Date(a.start_datetime);
          const db_ = b.start_datetime?.toDate ? b.start_datetime.toDate() : new Date(b.start_datetime);
          return da - db_;
        })
        .slice(0, 10); // cap at 10 upcoming events
      setMonthEvents(events);
    } catch (err) {
      console.error('AdminHome: events error', err);
      setMonthEvents([]);
    }
  };

  const metricsReady = memberMetrics && volunteerMetrics;

  if (!heroImageUrl) {
    return (
      <div className="bb-admin-home bb-admin-home--no-img">
        <div className="bb-admin-home-no-image">
          <div className="bb-admin-home-no-image-inner">
            <div className="bb-admin-home-no-image-icon">🖼️</div>
            <h3>Welcome to Your Admin Home</h3>
            <p>
              Upload a Hero Image to personalise this page. Go to{' '}
              <strong>Core → Image Management</strong> to upload your organisation's
              hero image.
            </p>
            <button className="bb-admin-home-goto-btn" onClick={onGoToImages}>
              Go to Image Management
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="bb-admin-home">
      <div className="bb-admin-home-container">
        <img
          src={heroImageUrl}
          alt="Organisation hero"
          className="bb-admin-home-image"
        />

        <div className="bb-admin-home-overlay">
          {loading ? (
            <div className="bb-admin-home-overlay-loading">
              <div className="bb-admin-home-spinner" />
            </div>
          ) : (
            <div className="bb-admin-home-metrics">
              {/* Row 1 — Members */}
              <div className="bb-admin-home-row-label">Members</div>
              <div className="bb-admin-home-metrics-row">
                <div className="bb-admin-home-metric-card">
                  <span className="bb-admin-home-metric-label">Total Members</span>
                  <span className="bb-admin-home-metric-value">
                    {metricsReady ? memberMetrics.total.toLocaleString() : <span className="bb-admin-home-metric-skeleton" />}
                  </span>
                </div>
                <div className="bb-admin-home-metric-card">
                  <span className="bb-admin-home-metric-label">New Members (This Year)</span>
                  {metricsReady
                    ? <span className="bb-admin-home-metric-value">{memberMetrics.newThisYear.toLocaleString()}</span>
                    : <span className="bb-admin-home-metric-skeleton" />}
                </div>
                <div className="bb-admin-home-metric-card">
                  <span className="bb-admin-home-metric-label">% Highly Engaged</span>
                  {metricsReady
                    ? <>
                        <span className="bb-admin-home-metric-value">{memberMetrics.pctHigh}%</span>
                        <span className="bb-admin-home-metric-sub">of all members</span>
                      </>
                    : <span className="bb-admin-home-metric-skeleton" />}
                </div>
                <div className="bb-admin-home-metric-card">
                  <span className="bb-admin-home-metric-label">% Medium Engaged</span>
                  {metricsReady
                    ? <>
                        <span className="bb-admin-home-metric-value">{memberMetrics.pctMedium}%</span>
                        <span className="bb-admin-home-metric-sub">of all members</span>
                      </>
                    : <span className="bb-admin-home-metric-skeleton" />}
                </div>
              </div>

              {/* Row 2 — Volunteers */}
              <div className="bb-admin-home-row-label" style={{ marginTop: 6 }}>Volunteers</div>
              <div className="bb-admin-home-metrics-row">
                <div className="bb-admin-home-metric-card">
                  <span className="bb-admin-home-metric-label">Total Volunteers</span>
                  <span className="bb-admin-home-metric-value">
                    {metricsReady ? volunteerMetrics.total.toLocaleString() : <span className="bb-admin-home-metric-skeleton" />}
                  </span>
                </div>
                <div className="bb-admin-home-metric-card">
                  <span className="bb-admin-home-metric-label">New Volunteers (This Year)</span>
                  {metricsReady
                    ? <span className="bb-admin-home-metric-value">{volunteerMetrics.newThisYear.toLocaleString()}</span>
                    : <span className="bb-admin-home-metric-skeleton" />}
                </div>
                <div className="bb-admin-home-metric-card">
                  <span className="bb-admin-home-metric-label">% Committed (51–100 hrs) of All Volunteers</span>
                  {metricsReady
                    ? <>
                        <span className="bb-admin-home-metric-value">{volunteerMetrics.pctHigh}%</span>
                        <span className="bb-admin-home-metric-sub">of all volunteers</span>
                      </>
                    : <span className="bb-admin-home-metric-skeleton" />}
                </div>
                <div className="bb-admin-home-metric-card">
                  <span className="bb-admin-home-metric-label">% Getting Started (1–50 hrs) of All Volunteers</span>
                  {metricsReady
                    ? <>
                        <span className="bb-admin-home-metric-value">{volunteerMetrics.pctMedium}%</span>
                        <span className="bb-admin-home-metric-sub">of all volunteers</span>
                      </>
                    : <span className="bb-admin-home-metric-skeleton" />}
                </div>
              </div>
            </div>
          )}

          {/* This Month's Events strip */}
          <div className="bb-admin-home-events">
            <div className="bb-admin-home-events-label">Upcoming Events</div>
            <div className="bb-admin-home-events-strip">
              {loading ? (
                <div className="bb-admin-home-events-empty">Loading events…</div>
              ) : monthEvents.length === 0 ? (
                <div className="bb-admin-home-events-empty">No upcoming events</div>
              ) : (
                monthEvents.map(ev => (
                  <div
                    key={ev.id}
                    className="bb-admin-home-event-card"
                    onClick={() => setSelectedEvent(ev)}
                    title="Click to view details"
                  >
                    <div className="bb-admin-home-event-name">{truncate(ev.title || ev.name || 'Event', 24)}</div>
                    <div className="bb-admin-home-event-date">{fmtEventDate(ev.start_datetime)}</div>
                    <div className="bb-admin-home-event-type">{CATEGORY_LABELS[ev.category] || ev.event_type || ev.category || ''}</div>
                    <div className={`bb-admin-home-event-status bb-admin-home-event-status--${ev.status || 'published'}`}>
                      {ev.status ? ev.status.charAt(0).toUpperCase() + ev.status.slice(1) : 'Published'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Event detail modal */}
    {selectedEvent && (
      <EventDetailModal ev={selectedEvent} onClose={() => setSelectedEvent(null)} />
    )}
    </>
  );
}

export default AdminHome;
